import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import moment from 'moment'
import ReactMarkdown from 'react-markdown'
import rehypeSanitize from 'rehype-sanitize'
import API from '../../services/API'
import RoadmapCard from './RoadMapCard'
import { UserContext } from '../../App'
import { SiteModeContext } from '../../components/mainFrame/MainFrame.jsx'
import { LATEST_WHATS_NEW_VERSION } from './WhatsNew/latestRelease'

import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'

import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded'
import DeveloperBoardRoundedIcon from '@mui/icons-material/DeveloperBoardRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded'
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded'
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded'
import GarageRoundedIcon from '@mui/icons-material/GarageRounded'
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded'
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded'
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded'
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded'
import NfcRoundedIcon from '@mui/icons-material/NfcRounded'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded'
import MedicationRoundedIcon from '@mui/icons-material/MedicationRounded'
import TvRoundedIcon from '@mui/icons-material/TvRounded'
import { ArrowRight } from '@mui/icons-material'
import wallboards from './WhatsNew/Wallboards.md'
import criticalCare from './WhatsNew/CriticalCare.md'
import finance from './WhatsNew/Finance.md'
import elearning from './WhatsNew/Elearning.md'
import governanceActions from './WhatsNew/GovernanceActions.md'
import medicines from './WhatsNew/Medicines.md'
import patientTransport from './WhatsNew/PatientTransport.md'
import fleet from './WhatsNew/Fleet.md'
import eventPlanning from './WhatsNew/EventPlanning.md'
import ambuTag from './WhatsNew/AmbuTag.md'
import cqcCentre from './WhatsNew/CqcCentre.md'
import epcrAudits from './WhatsNew/EpcrAudits.md'
import occupationalHealth from './WhatsNew/OccupationalHealth.md'
import AmbulanceIcon from '../../assets/icons/Ambulance'
import ReleaseRibbon from './WhatsNew/ReleaseRibbon'
import MarkdownS3Image from './WhatsNew/MarkdownS3Image'

const BRAND = {
  dark: '#101827',
  navy: '#1A2230',
  slate: '#242F40',
  teal: '#239AA4',
  tealLight: '#46D7E1',
  bg: '#F5F7FB',
}

const categoryColors = {
  Added: 'success',
  Improved: 'info',
  Fixed: 'warning',
  Security: 'error',
  Changed: 'secondary',
  Breaking: 'error',
}

const CHANGELOG_PAGE_SIZE = 25
const HIGHLIGHT_QUERY_PARAM = 'highlight'

const featuredUpdates = [
  {
    id: 'wallboards',
    title: 'Wallboards',
    description:
      'Turn any TV into a live display of your operation — rotating screens of shifts, fleet, KPIs and shoutouts, with a drag-and-drop editor and secure device unlock that leaves no one signed in.',
    icon: TvRoundedIcon,
    color: '#0891B2',
    points: [
      'Live rotating screens built from fourteen widgets',
      'Design on a true-to-TV canvas — drag, resize, done',
      'Authorise a display once; revoke it any time',
    ],
  },
  {
    id: 'critical-care',
    title: 'Critical Care ePCR',
    description:
      'A new Critical Care record type with live tap-first arrest and intervention consoles, drawn-up medicines, DOPS assessments, a personal procedure logbook and automatic case review.',
    icon: MonitorHeartRoundedIcon,
    color: '#7C3AED',
    points: [
      'Live arrest & critical care consoles — tap, don’t type',
      'Drawn-up medicines tracked from syringe to administration',
      'DOPS assessments and a personal procedure logbook',
    ],
  },
  {
    id: 'finance-invoicing',
    title: 'Finance & Invoicing',
    description:
      'One dependable invoice lifecycle for event and PTS work — editable drafts, immutable approved invoices, credit notes and reissue, credit-aware billing runs, and payroll and accounting exports.',
    icon: PaidRoundedIcon,
    color: '#7f0583',
    improved: true,
    points: [
      'Draft → Approved → Sent → Paid, for every invoice',
      'Corrections via credit note, with one-click reissue',
      'Pay-run and accounting-ready exports',
    ],
  },
  {
    id: 'elearning',
    title: 'E-learning & CPD Courses',
    description:
      'Seventeen AmbuNet-developed courses mapped to the CSTF, delivered in-app with fresh-draw assessments and verifiable certificates, feeding straight into Mandatory Training records.',
    icon: SchoolRoundedIcon,
    color: '#0EA5E9',
    points: [
      'Statutory and mandatory training, delivered in-app',
      'Role-based course requirements against your own roles',
      'Certificates land in Mandatory Training automatically',
    ],
  },
  {
    id: 'governance-actions',
    title: 'Governance Actions Register',
    description:
      'Every governance action — from audits, the CQC Centre or raised manually — tracked in one register with evidence on completion, and KPI counts that drill down to the records.',
    icon: AssignmentTurnedInRoundedIcon,
    color: '#4338CA',
  },
  {
    id: 'medicines-cd-register',
    title: 'CD Register & Seal Scanning',
    description:
      'A digital controlled drug register with live running balances and inspection-ready exports, a formal discrepancy workflow, calculated CD audits, QR seal scanning and a weekly expiry digest.',
    icon: MedicationRoundedIcon,
    color: '#B91C1C',
    points: [
      'Running balances that reconcile against physical stock',
      'Scan a seal to verify it — on web and in the crew app',
      'Discrepancies tracked from Open to Resolved',
    ],
  },
  {
    id: 'patient-transport-system',
    title: 'Patient Transport System',
    description:
      'Dedicated discharge crews, crew self-dispatch jobs, shared runs, live ETAs on dispatch and the hospital portal, repeat duties, and an optional reduced minimum dataset for sparse bookings.',
    icon: AmbulanceIcon,
    color: BRAND.teal,
    improved: true,
    points: [
      'Discharge crews the portal books straight onto',
      'Live ETAs for dispatch, wards and patients',
      'Crews can add jobs on the spot',
    ],
  },
  {
    id: 'fleet-make-ready',
    title: 'Fleet & Make Ready',
    description:
      'Redesigned vehicle pages, LOLER thorough examinations, crew fuel logging and fuel cards, and planned downtime with automatic service reminders.',
    icon: GarageRoundedIcon,
    color: '#2e6904',
    improved: true,
  },
  {
    id: 'event-planning',
    title: 'Event Planning Tools',
    description:
      'Organiser entry lists with bib-number lookup in ePCRs, a redesigned events calendar, shift bulk tools with per-slot assignment, and geocoded event addresses for maps and CAD.',
    icon: CelebrationRoundedIcon,
    color: '#B45309',
    improved: true,
  },
  {
    id: 'ambutag',
    title: 'AmbuTag',
    description:
      'NFC tags for your kit — tap a drug bag, asset or vehicle with your phone to open the right AmbuNet action instantly.',
    icon: NfcRoundedIcon,
    color: '#0F766E',
    points: [
      'Tap to sign out, check contents or log a defect',
      'Resolves to the records you already have',
      'Per-item scan history for free',
    ],
  },
  {
    id: 'cqc-centre',
    title: 'CQC Centre',
    description:
      'Continuously scored inspection readiness built from the evidence AmbuNet already holds, with a gap register, compliance self-assessment and Inspection Pack.',
    icon: VerifiedRoundedIcon,
    color: '#1D4ED8',
  },
  {
    id: 'epcr-audits',
    title: 'ePCR Record Audits',
    description:
      'Records are selected for clinical audit automatically at submission, with a structured six-point review, clinician feedback and self-audit prevention.',
    icon: FactCheckRoundedIcon,
    color: '#0E7490',
  },
  {
    id: 'occupational-health',
    title: 'Occupational Health',
    description:
      'Structured screening for every member of staff, automatic flags for HR review, appraisal-linked confirmation and a workforce-wide dashboard.',
    icon: HealthAndSafetyRoundedIcon,
    color: '#BE185D',
  },
]

export default function UpdatesHub() {
  const [tab, setTab] = useState(0)
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, setUser } = useContext(UserContext)
  const { mainFrameContainerRef } = useContext(SiteModeContext)

  useEffect(() => {
    if (!user?._id) return
    if (user.uiPreferences?.whatsNewSeenVersion === LATEST_WHATS_NEW_VERSION) {
      return
    }

    API.post('/account/uipreferences', {
      whatsNewSeenVersion: LATEST_WHATS_NEW_VERSION,
    }).then((res) => {
      if (res.status === 200) {
        setUser((prev) => ({ ...prev, uiPreferences: res.data }))
      }
    })
  }, [user?._id, user?.uiPreferences?.whatsNewSeenVersion])

  const selectedHighlight = searchParams.get(HIGHLIGHT_QUERY_PARAM) || ''
  const selectedHighlightExists = featuredUpdates.some(
    (item) => item.id === selectedHighlight,
  )
  const activeHighlight = selectedHighlightExists ? selectedHighlight : ''

  useEffect(() => {
    if (activeHighlight) {
      setTab(0)
    }
  }, [activeHighlight])

  const handleTabChange = (e, value) => {
    setTab(value)

    if (value !== 0 && selectedHighlight) {
      const nextSearchParams = new URLSearchParams(searchParams)
      nextSearchParams.delete(HIGHLIGHT_QUERY_PARAM)
      setSearchParams(nextSearchParams, { replace: true })
    }
  }

  const handleSelectHighlight = (highlightId) => {
    const nextSearchParams = new URLSearchParams(searchParams)
    if (highlightId) {
      nextSearchParams.set(HIGHLIGHT_QUERY_PARAM, highlightId)
      mainFrameContainerRef?.current?.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      nextSearchParams.delete(HIGHLIGHT_QUERY_PARAM)
    }
    setSearchParams(nextSearchParams)
  }

  return (
    <Box
      sx={{
        px: { xs: 1.5, md: 3 },
        py: { xs: 2, md: 3 },
        my: -1,
        mx: -2,
      }}
    >
      <Stack spacing={3}>
        <Hero />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 5,
            border: '1px solid rgba(15,23,42,0.08)',
            background: 'rgba(255,255,255,0.92)',
            boxShadow: '0 18px 60px rgba(15,23,42,0.08)',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 2,
              borderBottom: '1px solid rgba(15,23,42,0.08)',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 900,
                minHeight: 64,
              },
            }}
          >
            <Tab
              icon={<CampaignRoundedIcon />}
              iconPosition="start"
              label="What’s new"
            />
            {/* <Tab
              icon={<DeveloperBoardRoundedIcon />}
              iconPosition="start"
              label="Feature Requests"
            /> */}
            {/* <Tab
              icon={<HistoryRoundedIcon />}
              iconPosition="start"
              label="Changelog"
            /> */}
          </Tabs>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {tab === 0 && (
              <WhatsNew
                selectedHighlight={activeHighlight}
                onSelectHighlight={handleSelectHighlight}
              />
            )}
            {tab === 1 && <RoadmapSection />}
            {tab === 2 && <ChangelogSection />}
          </Box>
        </Paper>
      </Stack>
    </Box>
  )
}

function Hero() {
  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 5,
        p: { xs: 3, md: 5 },
        color: 'white',
        border: '1px solid rgba(255,255,255,0.18)',
        boxShadow: '0 30px 90px rgba(15,23,42,0.25)',
        background: `
          radial-gradient(circle at 16% 8%, rgba(70,215,225,0.22), transparent 24%),
          radial-gradient(circle at 92% 18%, rgba(124,58,237,0.18), transparent 22%),
          linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.slate} 52%, ${BRAND.dark} 100%)
        `,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: 280,
          height: 280,
          right: -90,
          top: -90,
          borderRadius: '50%',
          background: 'rgba(70,215,225,0.13)',
        }}
      />

      <Box position="relative">
        <Chip
          icon={
            <AutoAwesomeRoundedIcon
              sx={{ color: `${BRAND.tealLight} !important` }}
            />
          }
          label="AmbuNet Updates"
          sx={{
            mb: 2,
            backgroundColor: 'rgba(255,255,255,0.10)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.18)',
            fontWeight: 900,
          }}
        />

        <Typography
          variant="h3"
          fontWeight={950}
          color="white"
          sx={{
            letterSpacing: '-0.04em',
            fontSize: { xs: '2.1rem', md: '3.4rem' },
            lineHeight: 1,
          }}
        >
          What’s new in AmbuNet
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mt: 2,
            color: 'rgba(255,255,255,0.72)',
            maxWidth: 760,
            fontSize: { xs: '0.95rem', md: '1.05rem' },
          }}
        >
          See the latest improvements, vote on upcoming features, and follow the
          changes being released across the AmbuNet platform.
        </Typography>
      </Box>
    </Paper>
  )
}

function WhatsNew({ selectedHighlight, onSelectHighlight }) {
  const selectedUpdate = featuredUpdates.find(
    (item) => item.id === selectedHighlight,
  )

  return (
    <Stack spacing={3}>
      {selectedUpdate ? (
        <>
          <UpdateHighlight
            queryString="wallboards"
            selectedHighlight={selectedHighlight}
            title="Wallboards"
          >
            <MarkdownDoc source={wallboards} />
          </UpdateHighlight>

          <UpdateHighlight
            queryString="critical-care"
            selectedHighlight={selectedHighlight}
            title="Critical Care ePCR"
          >
            <MarkdownDoc source={criticalCare} />
          </UpdateHighlight>

          <UpdateHighlight
            queryString="finance-invoicing"
            selectedHighlight={selectedHighlight}
            title="Finance & Invoicing"
          >
            <MarkdownDoc source={finance} />
          </UpdateHighlight>

          <UpdateHighlight
            queryString="elearning"
            selectedHighlight={selectedHighlight}
            title="E-learning & CPD Courses"
          >
            <MarkdownDoc source={elearning} />
          </UpdateHighlight>

          <UpdateHighlight
            queryString="governance-actions"
            selectedHighlight={selectedHighlight}
            title="Governance Actions Register"
          >
            <MarkdownDoc source={governanceActions} />
          </UpdateHighlight>

          <UpdateHighlight
            queryString="medicines-cd-register"
            selectedHighlight={selectedHighlight}
            title="CD Register & Seal Scanning"
          >
            <MarkdownDoc source={medicines} />
          </UpdateHighlight>

          <UpdateHighlight
            queryString="patient-transport-system"
            selectedHighlight={selectedHighlight}
            title="Patient Transport System"
          >
            <MarkdownDoc source={patientTransport} />
          </UpdateHighlight>

          <UpdateHighlight
            queryString="fleet-make-ready"
            selectedHighlight={selectedHighlight}
            title="Fleet & Make Ready"
          >
            <MarkdownDoc source={fleet} />
          </UpdateHighlight>

          <UpdateHighlight
            queryString="event-planning"
            selectedHighlight={selectedHighlight}
            title="Event Planning Tools"
          >
            <MarkdownDoc source={eventPlanning} />
          </UpdateHighlight>

          <UpdateHighlight
            queryString="ambutag"
            selectedHighlight={selectedHighlight}
            title="AmbuTag"
          >
            <MarkdownDoc source={ambuTag} />
          </UpdateHighlight>

          <UpdateHighlight
            queryString="cqc-centre"
            selectedHighlight={selectedHighlight}
            title="CQC Centre"
          >
            <MarkdownDoc source={cqcCentre} />
          </UpdateHighlight>

          <UpdateHighlight
            queryString="epcr-audits"
            selectedHighlight={selectedHighlight}
            title="ePCR Record Audits"
          >
            <MarkdownDoc source={epcrAudits} />
          </UpdateHighlight>

          <UpdateHighlight
            queryString="occupational-health"
            selectedHighlight={selectedHighlight}
            title="Occupational Health"
          >
            <MarkdownDoc source={occupationalHealth} />
          </UpdateHighlight>

          <Box>
            <Button
              variant="outlined"
              onClick={() => onSelectHighlight('')}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 900,
              }}
            >
              All highlights
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box>
            <Typography variant="h5" fontWeight={950}>
              Latest highlights
            </Typography>
          </Box>
          <Box>
            <Grid container spacing={2}>
              {featuredUpdates.map((item) => (
                <Grid item xs={12} md={6} key={item.title}>
                  <FeatureCard
                    {...item}
                    onSelect={() => onSelectHighlight(item.id)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 4,
          border: '1px solid rgba(35,154,164,0.18)',
          background:
            'linear-gradient(135deg, rgba(35,154,164,0.08), rgba(70,215,225,0.06))',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={950} fontSize="1.15rem">
              Built around customer feedback
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Many of these updates have been shaped directly by requests,
              feedback and real-world workflows from AmbuNet users.
            </Typography>
          </Box>
          <Chip
            label="More updates released regularly"
            sx={{
              fontWeight: 900,
              bgcolor: 'rgba(35,154,164,0.12)',
              color: BRAND.teal,
            }}
          />
        </Stack>
      </Paper>
    </Stack>
  )
}

function UpdateHighlight({ title, queryString, selectedHighlight, children }) {
  if (selectedHighlight !== queryString) return null

  return children
}

function MarkdownDoc({ source }) {
  return (
    <Box
      sx={{
        p: { xs: 2 },
        borderRadius: 3,
        '& h1, & h2, & h3': {
          fontWeight: 950,
          mt: 2.5,
          mb: 1,
        },
        '& h1:first-of-type, & h2:first-of-type': { mt: 0 },
        '& p, & li': {
          color: 'text.secondary',
          fontSize: '0.95rem',
          lineHeight: 1.7,
        },
        '& ul, & ol': {
          pl: 3,
          mt: 1,
          mb: 2,
        },
        '& blockquote': {
          m: 0,
          my: 2,
          p: 2,
          borderRadius: 2,
          border: '1px solid rgba(35,154,164,0.22)',
          backgroundColor: 'rgba(35,154,164,0.06)',
        },
        '& blockquote p': {
          m: 0,
          color: 'text.primary',
          fontWeight: 700,
        },
        '& hr': {
          border: 0,
          borderTop: '1px solid rgba(15,23,42,0.1)',
          my: 3,
        },
        '& code': {
          px: 0.5,
          py: 0.15,
          borderRadius: 1,
          backgroundColor: 'rgba(15,23,42,0.06)',
        },
      }}
    >
      <ReactMarkdown
        rehypePlugins={[rehypeSanitize]}
        components={{
          img: MarkdownS3Image,
          p: ({ node, children }) => {
            const firstChild = node?.children?.[0]

            if (node?.children?.length === 1 && firstChild?.tagName === 'img') {
              return <>{children}</>
            }

            return <p>{children}</p>
          },
        }}
      >
        {source}
      </ReactMarkdown>
    </Box>
  )
}

function FeatureCard({
  title,
  id,
  description,
  improved,
  icon: Icon,
  color,
  points,
  onSelect,
}) {
  return (
    <Paper
      elevation={0}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
      sx={{
        position: 'relative',
        p: 2.5,
        borderRadius: 4,
        border: '1px solid rgba(15,23,42,0.08)',
        boxShadow: '0 12px 34px rgba(15,23,42,0.06)',
        background: '#fff',
        cursor: 'pointer',
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 16px 40px rgba(15,23,42,0.09)',
        },
        '&:focus-visible': {
          boxShadow: `0 0 0 4px ${color}26`,
        },
      }}
    >
      <ReleaseRibbon improved={improved} color={color} />
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 2.5,
              bgcolor: `${color}18`,
              color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon />
          </Box>

          <Typography fontWeight={950} fontSize="1.1rem">
            {title}
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>

        <Button
          sx={{ bgcolor: color, color: 'white' }}
          variant="contained"
          endIcon={<ArrowRight />}
          onClick={(e) => {
            e.stopPropagation()
            onSelect()
          }}
        >
          Read more
        </Button>
      </Stack>
    </Paper>
  )
}
const ROADMAP_COLUMNS = [
  {
    key: 'requested',
    title: 'Feature Requests',
    caption: 'Vote for what we build next',
    icon: HowToVoteRoundedIcon,
    color: '#268EBB',
    match: (task) => ['new', 'triaged', 'selected'].includes(task.status),
    sort: (a, b) => Number(b.voteCount || 0) - Number(a.voteCount || 0),
    empty: 'No open requests right now — suggest one through Support.',
  },
  {
    key: 'inProgress',
    title: 'In Progress',
    caption: 'Being designed, built and tested',
    icon: ConstructionRoundedIcon,
    color: '#4A63FF',
    match: (task) =>
      ['blocked/waiting', 'in progress', 'testing'].includes(task.status),
    sort: (a, b) => Number(b.date_updated) - Number(a.date_updated),
    empty: 'Nothing in build at the moment.',
  },
  {
    key: 'completed',
    title: 'Recently Completed',
    caption: 'Shipped in the last 3 months',
    icon: RocketLaunchRoundedIcon,
    color: '#00964C',
    match: (task) => String(task.status).toLowerCase() === 'closed',
    sort: (a, b) => Number(b.date_done) - Number(a.date_done),
    empty: 'Nothing shipped in the last 3 months.',
  },
]

const RoadmapColumnFrame = ({ column, count, children }) => {
  const Icon = column.icon
  return (
    <Grid item xs={12} lg={4}>
      <Box
        sx={{
          bgcolor: '#F8FAFC',
          borderRadius: 4,
          p: 1.5,
          height: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderTop: `3px solid ${column.color}`,
        }}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{ mb: 1.5, px: 0.5 }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              backgroundColor: `${column.color}18`,
              color: column.color,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box minWidth={0} flex={1}>
            <Typography fontWeight={900} fontSize="0.95rem" noWrap>
              {column.title}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              noWrap
            >
              {column.caption}
            </Typography>
          </Box>
          {count !== undefined && (
            <Chip
              label={count}
              size="small"
              sx={{
                fontWeight: 900,
                backgroundColor: `${column.color}18`,
                color: column.color,
              }}
            />
          )}
        </Stack>
        <Stack spacing={1.5}>{children}</Stack>
      </Box>
    </Grid>
  )
}

const RoadmapCardSkeleton = () => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      borderRadius: 3,
      p: 1.5,
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Skeleton
        variant="rounded"
        width={44}
        height={52}
        sx={{ borderRadius: 2, flexShrink: 0 }}
      />
      <Stack spacing={0.75} flex={1}>
        <Skeleton variant="text" width="75%" sx={{ fontSize: '0.9rem' }} />
        <Skeleton variant="text" width="100%" sx={{ fontSize: '0.75rem' }} />
        <Skeleton variant="text" width="55%" sx={{ fontSize: '0.75rem' }} />
        <Skeleton
          variant="rounded"
          width={72}
          height={18}
          sx={{ borderRadius: 1 }}
        />
      </Stack>
    </Stack>
  </Box>
)

function RoadmapSection() {
  const [loading, setLoading] = useState(true)
  const [tasks, setTasks] = useState([])
  const [fetchError, setFetchError] = useState(false)

  useEffect(() => {
    API.get('/dev/roadmaptasks')
      .then((res) => {
        if (res.status === 200) {
          setTasks(res.data)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" fontWeight={950}>
          Public Roadmap
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Vote on feature requests and follow what is in build and what has
          shipped recently.
        </Typography>
      </Box>

      {fetchError ? (
        <Typography color="error" fontWeight={700}>
          Unable to load the roadmap. Please try again later.
        </Typography>
      ) : (
        <Grid container spacing={2} alignItems="stretch">
          {ROADMAP_COLUMNS.map((column) => {
            if (loading) {
              return (
                <RoadmapColumnFrame key={column.key} column={column}>
                  <RoadmapCardSkeleton />
                  <RoadmapCardSkeleton />
                  <RoadmapCardSkeleton />
                </RoadmapColumnFrame>
              )
            }

            const columnTasks = tasks.filter(column.match).sort(column.sort)
            return (
              <RoadmapColumnFrame
                key={column.key}
                column={column}
                count={columnTasks.length}
              >
                {columnTasks.length === 0 ? (
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: '1px dashed',
                      borderColor: 'divider',
                      p: 2.5,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {column.empty}
                    </Typography>
                  </Box>
                ) : (
                  columnTasks.map((task) => (
                    <RoadmapCard
                      key={task.id}
                      task={task}
                      variant={column.key}
                      accent={column.color}
                    />
                  ))
                )}
              </RoadmapColumnFrame>
            )
          })}
        </Grid>
      )}
    </Stack>
  )
}

function ChangelogSection() {
  const [logs, setLogs] = useState([])
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(true)

  const loadPage = async (pageNumber) => {
    setLoading(true)
    const res = await API.get(
      `/changelog?page=${pageNumber}&limit=${CHANGELOG_PAGE_SIZE}`,
    )
    const { items, hasMore } = res.data

    setLogs((prev) => [...prev, ...items])
    setHasMore(hasMore)
    setLoading(false)
  }

  useEffect(() => {
    loadPage(0)
  }, [])

  const loadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    loadPage(nextPage)
  }

  const groupedArray = useMemo(() => {
    const grouped = logs.reduce((acc, log) => {
      const groupDate = moment(Number(log.date)).format('DD MMM YYYY')
      if (!acc[groupDate]) acc[groupDate] = []
      acc[groupDate].push(log)
      return acc
    }, {})

    return Object.entries(grouped)
      .map(([date, items]) => ({ date, items }))
      .sort(
        (a, b) => moment(b.date, 'DD MMM YYYY') - moment(a.date, 'DD MMM YYYY'),
      )
  }, [logs])

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" fontWeight={950}>
          Changelog
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          A transparent record of improvements, fixes and new features released
          across AmbuNet.
        </Typography>
      </Box>

      <Divider />

      {loading && logs.length === 0 && (
        <Stack spacing={1.5}>
          <Skeleton height={18} width="25%" />
          <Skeleton height={60} />
          <Skeleton height={60} />
        </Stack>
      )}

      <Stack spacing={3}>
        {groupedArray.map(({ date, items }) => (
          <Box key={date}>
            <Typography
              variant="subtitle2"
              fontWeight={800}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}
            >
              {date}
            </Typography>

            <Stack spacing={1}>
              {items.map((log, index) => (
                <Paper
                  key={`${date}-${index}`}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    borderLeftWidth: 4,
                    borderLeftStyle: 'solid',
                    borderLeftColor: (theme) =>
                      theme.palette[categoryColors[log.category] || 'primary']
                        .main,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    bgcolor: '#fff',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                  >
                    <Chip
                      label={log.category}
                      color={categoryColors[log.category] || 'primary'}
                      size="small"
                      sx={{
                        minWidth: '4.5rem',
                        fontWeight: 800,
                        borderRadius: 1.5,
                      }}
                    />

                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {log.text.trim()}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        ))}

        {!loading && logs.length === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No changelog entries yet.
          </Typography>
        )}
      </Stack>

      {hasMore && !loading && (
        <Box textAlign="center" mt={1}>
          <Button
            variant="outlined"
            onClick={loadMore}
            sx={{
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 900,
            }}
          >
            Load older changelog entries
          </Button>
        </Box>
      )}
    </Stack>
  )
}
